/**
 * Inventory records API hooks(REQ-20260421010)。
 *
 * 上傳流程(單張圖):
 *   1. POST /inventory-records/:id/attachment/presign → signed PUT URL
 *   2. PUT (direct to MinIO) + Content-Type 必須與 presign 時一致
 *   3. POST /inventory-records/:id/attachment → 建 DB row(storagePath 驗證)
 */
import axios from 'axios';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  ApiList,
  ApiSuccess,
  CreateInventoryRecordInput,
  InventoryRecordResponse,
  PresignInventoryUploadResponse,
  UpdateInventoryRecordInput,
} from '@app/shared';
import { INVENTORY_ALLOWED_MIMES } from '@app/shared';
import { apiClient } from '../../lib/api-client';

export class InventoryImageMimeError extends Error {
  constructor(actual: string) {
    super(`不支援的圖片類型:${actual}(僅接受 JPEG / PNG / WebP)`);
    this.name = 'InventoryImageMimeError';
  }
}

const PAGE_SIZE = 25;

/** 取某客戶存貨紀錄總筆數(供 tab 顯示);pageSize=1 最小 payload。 */
export function useInventoryRecordsTotal(customerId: number | undefined) {
  return useQuery({
    queryKey: ['inventory-records-total', customerId],
    enabled: customerId != null,
    staleTime: 1000 * 30,
    queryFn: async (): Promise<number> => {
      const res = await apiClient.get<ApiList<InventoryRecordResponse>>(
        `/customers/${customerId}/inventory-records`,
        { params: { page: 1, pageSize: 1 } },
      );
      return res.data.meta.total;
    },
  });
}

export function useInventoryRecords(customerId: number | undefined) {
  return useInfiniteQuery({
    queryKey: ['inventory-records', customerId],
    enabled: customerId != null,
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<ApiList<InventoryRecordResponse>> => {
      const res = await apiClient.get<ApiList<InventoryRecordResponse>>(
        `/customers/${customerId}/inventory-records`,
        { params: { page: pageParam, pageSize: PAGE_SIZE } },
      );
      return res.data;
    },
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.meta.page * lastPage.meta.pageSize;
      return loaded < lastPage.meta.total ? lastPage.meta.page + 1 : undefined;
    },
  });
}

export function useCreateInventoryRecord(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInventoryRecordInput): Promise<InventoryRecordResponse> => {
      const res = await apiClient.post<ApiSuccess<InventoryRecordResponse>>(
        `/customers/${customerId}/inventory-records`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['inventory-records', customerId],
        exact: false,
      });
      void qc.invalidateQueries({
        queryKey: ['inventory-records-total', customerId],
      });
    },
  });
}

export function useUpdateInventoryRecord(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      input: UpdateInventoryRecordInput;
    }): Promise<InventoryRecordResponse> => {
      const res = await apiClient.patch<ApiSuccess<InventoryRecordResponse>>(
        `/inventory-records/${args.id}`,
        args.input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['inventory-records', customerId],
        exact: false,
      });
      void qc.invalidateQueries({
        queryKey: ['inventory-records-total', customerId],
      });
    },
  });
}

export function useDeleteInventoryRecord(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/inventory-records/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['inventory-records', customerId],
        exact: false,
      });
      void qc.invalidateQueries({
        queryKey: ['inventory-records-total', customerId],
      });
    },
  });
}

export async function uploadInventoryImage(
  recordId: number,
  file: File,
): Promise<void> {
  // 型別守衛:File.type 是 string,後端 schema 只接受三種 MIME
  if (!(INVENTORY_ALLOWED_MIMES as readonly string[]).includes(file.type)) {
    throw new InventoryImageMimeError(file.type);
  }
  const mimeType = file.type as (typeof INVENTORY_ALLOWED_MIMES)[number];

  // 1) presign
  const presignRes = await apiClient.post<ApiSuccess<PresignInventoryUploadResponse>>(
    `/inventory-records/${recordId}/attachment/presign`,
    {
      originalName: file.name,
      mimeType,
      sizeBytes: file.size,
    },
  );
  const { uploadUrl, storedName, storagePath } = presignRes.data.data;

  // 2) PUT 到 MinIO(注意:Content-Type 必須與 presign 一致)
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': mimeType },
  });

  // 3) create DB row
  await apiClient.post(`/inventory-records/${recordId}/attachment`, {
    originalName: file.name,
    mimeType,
    sizeBytes: file.size,
    storedName,
    storagePath,
  });
}

export async function deleteInventoryImage(recordId: number): Promise<void> {
  await apiClient.delete(`/inventory-records/${recordId}/attachment`);
}

export async function getInventoryImageUrl(recordId: number): Promise<string> {
  const res = await apiClient.get<ApiSuccess<{ url: string; originalName: string }>>(
    `/inventory-records/${recordId}/attachment/signed-url`,
  );
  return res.data.data.url;
}

export function useInventoryImageUrl(recordId: number | null | undefined, hasImage: boolean) {
  return useQuery({
    queryKey: ['inventory-image-url', recordId],
    enabled: recordId != null && hasImage,
    // signed URL 5 分鐘過期,設 stale 4 分鐘
    staleTime: 1000 * 60 * 4,
    queryFn: () => getInventoryImageUrl(recordId!),
  });
}
